import { DEFAULT_TOASTER_SETTING, type IToasterSetting } from '@/constant/toaster'
import { endPoint } from '@/constant/end-point'
import { useActiveOrg } from '@/hooks/use-active-org'
import useAPI from '@/hooks/use-api'
import useApiMutation from '@/hooks/use-api-mutation'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

type IUpdateToasterSetting = Partial<Omit<IToasterSetting, 'isDefault'>>

/**
 * Hook for reading and writing the organisation's toast notification settings.
 *
 * Falls back to {@link DEFAULT_TOASTER_SETTING} while loading, when there is no active
 * organisation, and when the organisation has not saved a setting.
 *
 * Reads are open to any org user; writes and reset require ORG_ADMIN.
 */
export const useToasterSetting = () => {
  const { get } = useAPI<IToasterSetting>()
  const { activeOrg } = useActiveOrg()

  const { data, isLoading, error } = useQuery({
    queryKey: [endPoint.organisation.toasterSetting],
    queryFn: () => get(endPoint.organisation.toasterSetting),
    // Admin users have no organisation context, so there is nothing to read
    enabled: Boolean(activeOrg?.id),
  })

  const toasterSetting = (data?.data as unknown as IToasterSetting | undefined) ?? DEFAULT_TOASTER_SETTING

  const { updateMutation, deleteAllMutation } = useApiMutation<IUpdateToasterSetting>({
    endpoint: endPoint.organisation.toasterSetting,
  })

  const updateToasterSetting = useCallback(
    async (value: IUpdateToasterSetting) => {
      await updateMutation.mutateAsync(value)
    },
    [updateMutation]
  )

  const resetToasterSetting = useCallback(async () => {
    await deleteAllMutation.mutateAsync()
  }, [deleteAllMutation])

  return {
    toasterSetting,
    updateToasterSetting,
    resetToasterSetting,
    isLoading,
    error,
    isUpdating: updateMutation.isPending || deleteAllMutation.isPending,
  }
}
